import { Checkbox, Select, Table, Tag } from 'antd';
import type { TableProps } from 'antd/es/table';
import type React from 'react';
import { FormattedNumber } from 'react-intl';
import { compareByAlphabet } from 'src/helpers/sorting';
import type { GetPlatformsResponse, UpdateBrandsPayload } from 'src/types/platforms';

const compareByStatus = (status: string) => (status === 'active' ? 0 : 1);

const compareByPriority = (priority: string) =>
  priority === 'high' ? 3 : priority === 'normal' ? 2 : priority === 'low' ? 1 : 0;

const getEditedBrand = (editedBrands: UpdateBrandsPayload, id: number) => editedBrands.find((brand) => brand.id === id);

const getEffectiveCmsStatus = (record: GetPlatformsResponse[0]['brands'][0], editedBrands: UpdateBrandsPayload) =>
  getEditedBrand(editedBrands, record.id)?.cmsStatus || record.cmsStatus;

const getEffectivePriority = (record: GetPlatformsResponse[0]['brands'][0], editedBrands: UpdateBrandsPayload) =>
  getEditedBrand(editedBrands, record.id)?.priority || record.priority;

interface BrandsTableProps {
  data: GetPlatformsResponse[0]['brands'];
  editedBrands: UpdateBrandsPayload;
  onBrandsChange: (payload: UpdateBrandsPayload) => void;
}

export const BrandsTable: React.FC<BrandsTableProps> = ({ data, editedBrands, onBrandsChange }) => {
  const columns: TableProps<(typeof data)[0]>['columns'] = [
    {
      title: 'Brand',
      dataIndex: 'externalBrandName',
      key: 'externalBrandName',
      sorter: (a, b) => compareByAlphabet(a.externalBrandName, b.externalBrandName),
      filters: data
        ?.map(({ externalBrandName }) => ({ text: externalBrandName, value: externalBrandName }))
        .sort((a, b) => compareByAlphabet(a.value, b.value)),
      filterSearch: true,
      onFilter: (value, record) => record.externalBrandName === value,
      render: (value, record) => (
        <div className="flex items-center gap-x-4">
          <span className="truncate">{value}</span>
          {record.isNew && (
            <Tag color="success" className="text-[8px]/[12px] font-semibold px-[3px] bg-gray-1">
              NEW
            </Tag>
          )}
        </div>
      ),
      ellipsis: true,
      className: 'pl-64',
    },
    {
      title: 'Status',
      key: 'boStatus',
      dataIndex: 'boStatus',
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive in BO', value: 'inactive' },
      ],
      onFilter: (value, record) => record.boStatus === value,
      render: (value) =>
        value === 'active' ? (
          <span className="flex items-center gap-x-6">
            <span className="inline-block size-12 rounded-full bg-green-6" />
            <span className="text-[12px]/[20px]">Active</span>
          </span>
        ) : (
          <span className="flex items-center gap-x-6">
            <span className="inline-block size-12 rounded-full bg-red-6" />
            <span className="text-[12px]/[20px] text-red-6">Inactive in BO</span>
          </span>
        ),
      width: 168,
    },
    {
      title: (
        <div className="text-left">
          <div className="text-[12px]/[16px]">Hide from</div>
          <div className="text-[12px]/[16px]">CMS</div>
        </div>
      ),
      key: 'cmsStatus',
      dataIndex: 'cmsStatus',
      sorter: (a, b) =>
        compareByStatus(getEffectiveCmsStatus(a, editedBrands)) -
        compareByStatus(getEffectiveCmsStatus(b, editedBrands)),
      filters: [
        { text: 'Visible', value: 'active' },
        { text: 'Hidden', value: 'inactive' },
      ],
      onFilter: (value, record) => getEffectiveCmsStatus(record, editedBrands) === value,
      render: (_, record) => {
        const cmsStatus = getEffectiveCmsStatus(record, editedBrands) === 'inactive';

        return (
          <Checkbox
            checked={cmsStatus}
            onChange={(e) => onBrandsChange([{ id: record.id, cmsStatus: e.target.checked ? 'inactive' : 'active' }])}
          />
        );
      },
      align: 'center',
      className: 'py-0 px-8',
      width: 108,
    },
    {
      title: null,
      key: 'stub',
      dataIndex: 'stub',
      width: 116,
    },
    {
      title: 'Priority',
      key: 'priority',
      dataIndex: 'priority',
      sorter: (a, b) =>
        compareByPriority(getEffectivePriority(a, editedBrands)) -
        compareByPriority(getEffectivePriority(b, editedBrands)),
      sortDirections: ['descend', 'ascend'],
      filters: [
        { text: 'High', value: 'high' },
        { text: 'Normal', value: 'normal' },
        { text: 'Low', value: 'low' },
      ],
      onFilter: (value, record) => getEffectivePriority(record, editedBrands) === value,
      render: (_, record) => {
        const priority = getEffectivePriority(record, editedBrands);

        return (
          <Select
            value={priority}
            options={[
              {
                label: (
                  <Tag color="success" className="font-normal">
                    High
                  </Tag>
                ),
                value: 'high',
              },
              { label: <Tag className="font-normal">Normal</Tag>, value: 'normal' },
              {
                label: (
                  <Tag color="error" className="font-normal">
                    Low
                  </Tag>
                ),
                value: 'low',
              },
            ]}
            onChange={(value) => onBrandsChange([{ id: record.id, priority: value }])}
            size="small"
            classNames={{ root: 'border-0 shadow-none pl-0 bg-transparent' }}
            className="min-w-[102px]"
          />
        );
      },
      className: 'py-0',
      width: 176,
    },
    {
      title: (
        <div>
          <div className="text-[12px]/[16px]">Players</div>
          <div className="text-[12px]/[16px] font-normal">Last 30 days</div>
        </div>
      ),
      key: 'activeUsers30D',
      dataIndex: 'activeUsers30D',
      sorter: (a, b) => a.activeUsers30D - b.activeUsers30D,
      render: (value) => <FormattedNumber value={value} />,
      align: 'right',
      className: 'py-0 pr-8',
      width: 160,
    },
  ];

  return (
    <Table
      rowKey="externalBrandName"
      dataSource={data}
      columns={columns}
      pagination={false}
      showSorterTooltip={false}
      sticky
    />
  );
};
